/**
 * Push-varsler per enhet. Av til brukeren slår dem på under tannhjulet øverst,
 * og brukeren velger selv hva det varsles om. Abonnementet lagres i
 * `potte_varsel_abonnement`; Edge Function `potte-varsler` sjekker hvert 5. minutt
 * (regler i supabase/functions/potte-varsler/regler.ts).
 *
 * iPhone: virker bare når appen er lagt til på hjemskjermen (iOS 16.4+).
 */
import { supabase } from './supabase';

/** Offentlig VAPID-nøkkel. Den private ligger i Vault (potte_varsel_vapid_privat). */
export const VAPID_OFFENTLIG =
  'BOabee_m61Lu9Kqnib34UTCOVkQ7pDswGFnztC8IE-hTeLB1Re75m8dn-WZztW206oa6kIgkVgDC4AYlheQDuvA';

export type VarselType = 'frakoblet' | 'lite_vann' | 'varme';

export const VARSELVALG: { type: VarselType; navn: string; forklaring: string }[] = [
  { type: 'frakoblet', navn: 'Kassa er frakoblet', forklaring: 'Ingen data på 30 minutter, og når den er tilbake' },
  { type: 'lite_vann', navn: 'Lite vann', forklaring: 'Tanken er under 20 %' },
  { type: 'varme', navn: 'For varmt', forklaring: 'Over 32 °C ved kassa' },
];

export const STANDARDVALG: VarselType[] = ['frakoblet', 'lite_vann'];

export type Stotte = 'ok' | 'hjemskjerm' | 'nei';

/** Kan denne enheten få push? «hjemskjerm» = iPhone i Safari: legg til på hjemskjermen først. */
export function pushStotte(
  nav: Navigator = navigator,
  win: Window = window,
): Stotte {
  const harApi = 'serviceWorker' in nav && 'PushManager' in win && 'Notification' in win;
  if (harApi) return 'ok';
  const ios = /iPad|iPhone|iPod/.test(nav.userAgent);
  const frittstaende = win.matchMedia?.('(display-mode: standalone)').matches
    || (nav as Navigator & { standalone?: boolean }).standalone === true;
  if (ios && !frittstaende) return 'hjemskjerm';
  return 'nei';
}

export function tilUint8(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raa = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raa, (c) => c.charCodeAt(0));
}

async function registrering(): Promise<ServiceWorkerRegistration> {
  const eks = await navigator.serviceWorker.getRegistration();
  if (eks) return eks;
  // I utvikling registreres ikke sw.js automatisk (se main.ts).
  return navigator.serviceWorker.register('/sw.js');
}

export interface Abonnement {
  endpoint: string;
  valg: VarselType[];
  aktiv: boolean;
}

/** Abonnementet for denne enheten, eller null hvis varsler er av her. */
export async function hentAbonnement(): Promise<Abonnement | null> {
  if (pushStotte() !== 'ok') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const { data } = await supabase.from('potte_varsel_abonnement')
    .select('endpoint, valg, aktiv').eq('endpoint', sub.endpoint).maybeSingle();
  if (!data) return null;
  return { endpoint: data.endpoint, valg: (data.valg ?? []) as VarselType[], aktiv: data.aktiv };
}

/** Ber om lov, abonnerer og lagrer. Kaster med norsk melding hvis det ikke går. */
export async function slaaPaa(valg: VarselType[]): Promise<Abonnement> {
  if (pushStotte() === 'hjemskjerm') throw new Error('Legg appen til på hjemskjermen først (Del → Legg til på Hjem-skjerm).');
  if (pushStotte() !== 'ok') throw new Error('Denne nettleseren støtter ikke varsler.');
  const lov = await Notification.requestPermission();
  if (lov !== 'granted') throw new Error('Varsler er ikke tillatt. Slå dem på i telefonens innstillinger for appen.');
  const reg = await registrering();
  await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription())
    ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: tilUint8(VAPID_OFFENTLIG) as BufferSource,
    });
  const json = sub.toJSON();
  const rad = {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    valg,
    aktiv: true,
    enhet: navigator.userAgent.slice(0, 120),
  };
  const { error } = await supabase.from('potte_varsel_abonnement').upsert(rad, { onConflict: 'endpoint' });
  if (error) throw new Error('Fikk ikke lagret varslene. Sjekk nettet og prøv igjen.');
  return { endpoint: sub.endpoint, valg, aktiv: true };
}

export async function lagreValg(endpoint: string, valg: VarselType[]): Promise<void> {
  const { error } = await supabase.from('potte_varsel_abonnement').update({ valg }).eq('endpoint', endpoint);
  if (error) throw new Error('Fikk ikke lagret valget. Sjekk nettet og prøv igjen.');
}

/** Slår av på denne enheten: sletter raden og melder av hos nettleseren. */
export async function slaaAv(endpoint: string): Promise<void> {
  const { error } = await supabase.from('potte_varsel_abonnement').delete().eq('endpoint', endpoint);
  if (error) throw new Error('Fikk ikke slått av varslene. Sjekk nettet og prøv igjen.');
  const reg = await navigator.serviceWorker.getRegistration();
  await (await reg?.pushManager.getSubscription())?.unsubscribe();
}

export async function sendTest(endpoint: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('potte-varsler', { body: { test: true, endpoint } });
  if (error || !data?.sendt) throw new Error('Testvarselet kom ikke fram. Prøv å slå varslene av og på igjen.');
}
